import type { FormEvent } from "react";

import { Button, ButtonTypes } from "../basic/button";
import { Loading } from "../basic/loading";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../basic/otp";
import { LabelPositionEnum, ToggleSlider } from "../basic/toggleSlider";
import { LoginBackground } from "./background";
import { MfaOption } from "./mfaType";

interface Props {
	password: string;
	setPassword: (value: string) => void;
	submitFunction: (event: FormEvent<HTMLFormElement>) => void;

	rememberMe?: boolean;
	setRememberMe?: (value: boolean) => void;

	resendCode?: () => void;
	resendLoading?: boolean;
	type?: MfaOption;
	obfuscation?: string;

	goBack?: () => void;
	loading: boolean;
	disabled: boolean;
	version?: string;
	error?: string;
	success?: string;

	logoImage?: string;
	backgroundImage?: string;
}

export function OneTimePasswordPage(props: Props) {
	const {
		password,
		setPassword,
		submitFunction,
		rememberMe,
		setRememberMe,
		resendCode,
		resendLoading,
		type,
		obfuscation,
		goBack,
		loading,
		disabled,
		version,
		error,
		success,
		logoImage,
		backgroundImage,
	} = props;

	const typeString = type === MfaOption.Sms ? "your phone" : "your email";
	const obfuscationString = obfuscation ? ` (${obfuscation})` : "";

	return (
		<LoginBackground
			title="Multi Factor Code"
			description={`Your code was sent to ${typeString}${obfuscationString}. Enter it below to login`}
			version={version}
			logoImage={logoImage}
			backgroundImage={backgroundImage}
		>
			<form className="tw-grid tw-gap-4" onSubmit={submitFunction}>
				<div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
					<InputOTP maxLength={6} value={password} onChange={setPassword}>
						<InputOTPGroup>
							<InputOTPSlot index={0} />
							<InputOTPSlot index={1} />
							<InputOTPSlot index={2} />
							<InputOTPSlot index={3} />
							<InputOTPSlot index={4} />
							<InputOTPSlot index={5} />
						</InputOTPGroup>
					</InputOTP>
				</div>
				{error !== undefined && <span className="tw-text-sm tw-text-red-400">{error}</span>}
				{success !== undefined && <span className="tw-text-sm tw-text-green-400">{success}</span>}
				{rememberMe !== undefined && (
					<ToggleSlider
						id={"rememberMeCheckbox"}
						label={"Remember Machine"}
						value={rememberMe}
						changeHandler={setRememberMe}
						labelPosition={LabelPositionEnum.hidden}
						inline
						rounded
						className="!tw-m-0"
					/>
				)}
				<Loading loading={resendLoading} message="Sending code..." tag="h5">
					{resendCode && (
						<Button
							className="tw-w-full"
							types={[ButtonTypes.Text]}
							onClick={resendCode}
							disabled={loading}
						>
							Resend Code
						</Button>
					)}
				</Loading>
				<Loading loading={loading} message="Verifying code..." tag="h5">
					<Button
						type="submit"
						className="tw-w-full"
						types={[ButtonTypes.Primary]}
						disabled={disabled || loading || resendLoading}
					>
						Submit
					</Button>
				</Loading>

				{goBack && (
					<Button className="tw-w-full" types={[ButtonTypes.Outline]} onClick={goBack}>
						Go Back
					</Button>
				)}
			</form>
		</LoginBackground>
	);
}
